/**
 * API Client with Authentication and Rate Limiting
 * 
 * This module provides a centralized API client that handles:
 * - API key authentication
 * - Rate limiting to prevent abuse
 * - Request/response intercepting
 * - Error handling
 */

import { RateLimiter } from './rateLimiter';
import { getApiUrl } from './config';

// Configuration - Use the new HTTPS-aware configuration
const API_BASE = getApiUrl();
// API_KEY removed for testing payment processor logic
// const API_KEY = process.env.REACT_APP_API_KEY || '';

// Rate limiter configuration
const RATE_LIMIT_CONFIG = {
  maxRequests: parseInt(process.env.REACT_APP_RATE_LIMIT_MAX_REQUESTS) || 400, // Max requests per window
  windowMs: parseInt(process.env.REACT_APP_RATE_LIMIT_WINDOW_MS) || 60000, // 1 minute window
  burstLimit: parseInt(process.env.REACT_APP_RATE_LIMIT_BURST) || 20, // Burst limit for quick succession
  burstWindowMs: parseInt(process.env.REACT_APP_RATE_LIMIT_BURST_WINDOW_MS) || 10000 // 10 seconds burst window
};

// Create rate limiter instance
const rateLimiter = new RateLimiter(RATE_LIMIT_CONFIG);

/**
 * Custom error class for API-related errors
 */
export class ApiError extends Error {
  constructor(message, status, response) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.response = response;
  }
}

/**
 * Custom error class for rate limit errors
 */
export class RateLimitError extends Error {
  constructor(message, retryAfter) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}
/**
 * Global 401 error handler
 * This function handles token expiration by clearing auth data and redirecting to sign-in
 */
const handleTokenExpiration = () => {
  // Clear all auth-related data from localStorage
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_data');
  localStorage.removeItem('subscription_data');
  
  // Dispatch a custom event that the AuthContext can listen to
  window.dispatchEvent(new CustomEvent('auth:token-expired'));
  
  // Redirect to home page (which will show the landing page for unauthenticated users)
  // Use a small delay to ensure the auth context has time to update
  setTimeout(() => {
    window.location.href = '/';
  }, 100);
};

/**
 * API Client class that handles authentication and rate limiting
 */
class ApiClient {
  constructor() {
    this.baseURL = API_BASE;
    // API key removed for testing payment processor logic
    // this.apiKey = API_KEY;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Add API key to default headers if available - REMOVED for testing
    // if (this.apiKey) {
    //   this.defaultHeaders['X-API-Key'] = this.apiKey;
    // }
  }

  /**
   * Get headers for a request, including authentication
   */
  getHeaders(customHeaders = {}, endpoint = '') {
    const headers = { ...this.defaultHeaders, ...customHeaders };
    
    // Don't add auth token for public endpoints
    const isPublicEndpoint = endpoint.includes('/public/');
    
    // Add user auth token if available (from AuthContext) and not a public endpoint
    const authToken = localStorage.getItem('auth_token');
    if (authToken && !isPublicEndpoint) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    return headers;
  }

  /**
   * Check rate limit before making a request
   */
  async checkRateLimit(endpoint) {
    const rateLimitResult = rateLimiter.checkLimit(endpoint);
    
    if (!rateLimitResult.allowed) {
      throw new RateLimitError(
        `Rate limit exceeded for ${endpoint}. Please try again later.`,
        rateLimitResult.retryAfter
      );
    }

    return rateLimitResult;
  }

  /**
   * Make an HTTP request with rate limiting and error handling
   */
  async request(endpoint, options = {}) {
    try {
      // Check rate limit
      await this.checkRateLimit(endpoint);

      // Prepare request
      const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
      const config = {
        ...options,
        headers: this.getHeaders(options.headers, endpoint)
      };

      // Add request timestamp for monitoring
      const requestStart = Date.now();

      // Make the request
      const response = await fetch(url, config);
      const requestDuration = Date.now() - requestStart;

      // Log request for debugging (only in development)
      if (process.env.NODE_ENV === 'development') {
        console.log(`API Request: ${config.method || 'GET'} ${url} (${requestDuration}ms)`);
      }

      // Handle response
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorData = null;

        try {
          errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // Response body is not JSON, use status text
        }
        // Handle 401 errors globally (token expiration)
        if (response.status === 401) {
          // Only handle token expiration for authenticated endpoints
          // Skip auth verification endpoint to avoid infinite loops
          if (!endpoint.includes('/auth/verify')) {
            console.log('Token expired, redirecting to sign-in...');
            handleTokenExpiration();
          }
        }
        
        throw new ApiError(errorMessage, response.status, errorData);
      }

      // Parse response
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return await response.text();

    } catch (error) {
      // Re-throw custom errors
      if (error instanceof RateLimitError || error instanceof ApiError) {
        throw error;
      }

      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new ApiError('Network error. Please check your connection.', 0, null);
      }

      // Handle other errors
      throw new ApiError(error.message || 'An unexpected error occurred', 0, null);
    }
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    const url = new URL(endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`);
    
    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });

    return this.request(url.toString(), { method: 'GET' });
  }

  /**
   * POST request
   */
  async post(endpoint, data = null) {
    const options = {
      method: 'POST',
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    return this.request(endpoint, options);
  }

  /**
   * PUT request
   */
  async put(endpoint, data = null) {
    const options = {
      method: 'PUT',
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    return this.request(endpoint, options);
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data = null) {
    const options = {
      method: 'PATCH',
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    return this.request(endpoint, options);
  }

  /**
   * Get current rate limit status
   */
  getRateLimitStatus(endpoint) {
    return rateLimiter.getStatus(endpoint);
  }

  /**
   * Reset rate limit for an endpoint (useful for testing)
   */
  resetRateLimit(endpoint) {
    return rateLimiter.reset(endpoint);
  }

  /**
   * Check if API key is configured - REMOVED for testing
   */
  hasApiKey() {
    return false; // Always return false since API key is disabled
  }

  /**
   * Update API key (useful for dynamic configuration) - REMOVED for testing
   */
  setApiKey(apiKey) {
    // API key functionality disabled for testing
    console.warn('API key functionality is disabled for testing');
  }

  /**
   * Update base URL (useful for switching between HTTP/HTTPS)
   */
  updateBaseUrl() {
    this.baseURL = getApiUrl();
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Client base URL updated to: ${this.baseURL}`);
    }
  }

  /**
   * Get current base URL
   */
  getBaseUrl() {
    return this.baseURL;
  }
}

// Create and export a singleton instance
const apiClient = new ApiClient();

export { apiClient, ApiClient, RATE_LIMIT_CONFIG };
export default apiClient;
